import { el, clear } from "../components/dom.js";
import { persistedState } from "../utils/list-prefs.js";
import { bulkSelect } from "../components/bulk-select.js";
import { randomPickButton } from "../components/random-pick.js";
import { BOOK_OWNERSHIP_STATUSES } from "../components/status-badge.js";
import { filterBar } from "../components/filter-bar.js";
import { groupToggle } from "../components/group-toggle.js";
import { parseFilters, matchesAllFilters } from "../utils/filters.js";
import { listBooks } from "../db/books.js";
import { listGroups, categoryGroups } from "../db/groups.js";
import { listAllMedia } from "../db/media.js";
import { getRankingState } from "../db/ranking.js";
import { enableRowDrag } from "../utils/drag-drop.js";
import { sortedEntries } from "../utils/group-sort.js";
import { handleRowDrop } from "./group-dnd.js";
import { renderCategoryEntries } from "../components/mag-gallery.js";
import { viewToggle } from "../components/view-toggle.js";
import { getListView, setListView } from "../utils/view-mode.js";
import { navigate, navigateBack, getQueryParams } from "../main.js";
import { saveScroll, consumeScroll } from "../utils/scroll.js";

const SCROLL_KEY = "books-list";
const EMPTY_IDS = new Set(); // shared "collapse nothing" set for ungrouped view

const SORTS = [
	{ id: "created_at", label: "Date added" },
	{ id: "title", label: "Title A–Z" },
	{ id: "published_year", label: "Publish year" },
	{ id: "last_engaged_date", label: "Last read" },
	{ id: "rating", label: "Rating" },
	{ id: "battle_rank", label: "Battle rank" },
];

const state = persistedState("books-list", {
	sort: "created_at",
	// "gallery" (masonry tiles) or "list" (glass rows) — mirrors the device-wide
	// setting in utils/view-mode.js; re-read every time the screen renders.
	view: "gallery",
	// false = collections show as group cards (members collapsed); true = groups
	// are dissolved and every member shows as its own item in the list.
	expandGroups: false,
});

export async function renderBooksList(root) {
	const header = el(
		"header.app-header",
		{},
		el(
			"button.app-header__back",
			{ type: "button", onclick: () => navigateBack("/") },
			"‹",
		),
		el("h1", {}, "Books"),
		el(
			"button.app-header__icon",
			{
				type: "button",
				title: "VS Battle",
				onclick: () => navigate("/books/vs"),
			},
			"VS",
		),
		randomPickButton("book"),
		el(
			"button.app-header__icon",
			{
				type: "button",
				title: "Stats",
				onclick: () => navigate("/books/stats"),
			},
			"📊",
		),
		el(
			"button.app-header__action",
			{ type: "button", onclick: () => navigate("/books/add") },
			"+ Add",
		),
	);

	const sortSelect = el(
		"select.toolbar__sort",
		{
			value: state.sort,
			onchange: (e) => {
				state.sort = e.currentTarget.value;
				renderList();
			},
		},
		...SORTS.map((s) =>
			el("option", { value: s.id, selected: state.sort === s.id }, s.label),
		),
	);

	// Active-chips + "+ Filter" panel (reading status / ownership / awards).
	// Seeded from any deep-link facet in the URL (e.g. /books?status=done).
	const filter = filterBar({
		mediaType: "book",
		ownershipStatuses: BOOK_OWNERSHIP_STATUSES,
		initial: parseFilters(getQueryParams()),
		onChange: () => renderList(),
	});

	// Grouped ⇄ ungrouped view toggle (hidden when there are no groups).
	const groupBtn = groupToggle({
		expanded: state.expandGroups,
		onChange: (expanded) => {
			state.expandGroups = expanded;
			renderList();
		},
	});

	// Gallery ⇄ list layout toggle — one device-wide setting shared by every
	// category list (utils/view-mode.js).
	state.view = getListView();
	const viewBtn = viewToggle({
		mode: state.view,
		onChange: (mode) => {
			state.view = setListView(mode);
			renderList();
		},
	});

	// Select several items → status / ownership / group / delete (roadmap #17).
	const bulk = bulkSelect({ mediaType: "book" });

	const toolbar = el(
		"div.toolbar",
		{},
		el(
			"div.toolbar__row",
			{},
			el("label.toolbar__sort-wrap", {}, el("span", {}, "Sort: "), sortSelect),
			groupBtn,
			viewBtn,
			bulk.button,
			filter.button,
		),
		filter.panel,
		filter.chips,
	);

	const listContainer = el("div.games-list", {});
	bulk.attach(listContainer);
	const view = el("div.view", {}, header, toolbar, listContainer);
	root.append(view);

	let allBooks = [];
	let rankIndex = new Map(); // bookId → battle-ranking position (best = 0)
	// Book-scoped groups shown inline at the top of the list, and the member ids
	// they collapse out of the flat list below.
	let groupCards = [];
	let collapsedIds = new Set();

	function renderList() {
		clear(listContainer);

		const openGroup = (group) => {
			saveScroll(SCROLL_KEY);
			navigate(`/groups/${group.id}`);
		};
		const openBook = (book) => {
			saveScroll(SCROLL_KEY);
			navigate(`/books/${book.id}`);
		};

		// The toggle is only meaningful when the category actually has groups.
		groupBtn.hidden = groupCards.length === 0;

		// Ungrouped view: dissolve the groups — show no group cards and collapse
		// nothing, so every member surfaces as its own item.
		const activeGroups = state.expandGroups ? [] : groupCards;
		const activeCollapsed = state.expandGroups ? EMPTY_IDS : collapsedIds;

		const filters = filter.getFilters();
		const filteredItems = allBooks.filter(
			(b) => !activeCollapsed.has(b.id) && matchesAllFilters(b, filters),
		);
		// Groups sort inline among the items by the active sort (each group ranks
		// as its leading member); they're no longer pinned to the top.
		const entries = sortedEntries({
			items: filteredItems,
			groupCards: activeGroups,
			sort: state.sort,
			rankIndex,
			sortFn: sortBooks,
		});

		// Gallery tiles or glass rows (the toolbar toggle); groups render inline in
		// both, and every node carries data-drag-id/kind so drag-to-group works.
		renderCategoryEntries(listContainer, entries, {
			mediaType: "book",
			view: state.view,
			count: filteredItems.length,
			onOpenItem: openBook,
			onOpenGroup: openGroup,
			empty: () => emptyState(allBooks.length === 0),
		});

		const savedY = consumeScroll(SCROLL_KEY);
		if (savedY != null) {
			requestAnimationFrame(() =>
				requestAnimationFrame(() => window.scrollTo(0, savedY)),
			);
		}
	}

	const [books, ranking, groups, allMedia] = await Promise.all([
		listBooks(),
		getRankingState("book"),
		listGroups(),
		listAllMedia(),
	]);
	allBooks = books;
	rankIndex = new Map(ranking.order.map((id, i) => [id, i]));
	({ cards: groupCards, collapsedIds } = categoryGroups(groups, allMedia, "book"));

	// Re-fetch groups and re-render after a drag-drop change (item membership /
	// nesting). The library list itself is unchanged, so allMedia is reused.
	async function refreshGroups() {
		const fresh = await listGroups();
		({ cards: groupCards, collapsedIds } = categoryGroups(fresh, allMedia, "book"));
		renderList();
	}
	enableRowDrag(listContainer, {
		onDrop: (payload) => handleRowDrop(payload, { allMedia, refresh: refreshGroups }),
	});

	renderList();
}

function sortBooks(books, sort, rankIndex) {
	const copy = books.slice();
	switch (sort) {
		case "title":
			return copy.sort((a, b) => a.title.localeCompare(b.title));
		case "published_year":
			return copy.sort(
				(a, b) => (b.published_year ?? 0) - (a.published_year ?? 0),
			);
		case "last_engaged_date":
			return copy.sort((a, b) =>
				(b.last_engaged_date ?? "").localeCompare(a.last_engaged_date ?? ""),
			);
		case "rating":
			return copy.sort((a, b) => (b.rating ?? -1) - (a.rating ?? -1));
		case "battle_rank":
			return copy.sort((a, b) => {
				const ra = rankIndex.has(a.id) ? rankIndex.get(a.id) : Infinity;
				const rb = rankIndex.has(b.id) ? rankIndex.get(b.id) : Infinity;
				if (ra !== rb) return ra - rb;
				return a.title.localeCompare(b.title);
			});
		case "created_at":
		default:
			return copy.sort((a, b) =>
				(b.created_at ?? "").localeCompare(a.created_at ?? ""),
			);
	}
}

function emptyState(noBooksAtAll) {
	return el(
		"div.empty",
		{},
		el(
			"p",
			{},
			noBooksAtAll
				? "No books yet. Tap + Add to find one."
				: "No books match these filters.",
		),
	);
}
